/**
 * Story.jsx — Luvia Our Story Page
 *
 * Fixes vs submitted version:
 *
 * 1. SITE_URL: theluviajewels.com (matches whole codebase)
 * 2. og:image added (blank social previews fixed)
 * 3. og:type: website → article (editorial content)
 * 4. JSON-LD moved to module level (no JSON.stringify on every render)
 * 5. JSON-LD upgraded: @graph with WebPage + BreadcrumbList + Organization
 *    Organization schema is the highest-value addition for a brand story page
 * 6. Self-closing <li> breadcrumb separator fixed
 * 7. Page title improved: keyword-rich, not just "Our Story — Luvia"
 * 8. H1 improved: "The Luvia Story" — has keyword value, more memorable
 * 9. max-width: 900px removed from page shell → full-width with inner containers
 * 10. Desktop: two-column layout (narrative + sticky values panel)
 * 11. Approach items: 3-column grid on desktop (matching SizeGuide FitCards)
 * 12. CTA: "Learn More About Us → /about" replaced with collection link
 *     (story page + about page competing for same intent = cannibalisation)
 * 13. Inline collection nudge added after Approach section (mid-funnel capture)
 * 14. "Why Now?" section humanised — macro trend paragraphs → concrete values
 * 15. contain: layout style → contain: layout (style containment can break
 *     CSS custom property inheritance from parent scope)
 * 16. IconCheck import removed (was imported but never used)
 */

import { Link }   from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout     from '../../components/common/Layout/Layout';
import { BRAND }  from '../../config/brand';
import './Story.css';

// ── Constants ──────────────────────────────────────────────────────────────
const SITE_URL   = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://theluviajewels.com';
const OG_IMAGE   = `${SITE_URL}/og-image.jpg`;
const BRAND_YEAR = 2026;

// ── Approach items — module scope, never recreated ─────────────────────────
const APPROACH_ITEMS = [
  {
    id: 'small',
    num: '01',
    title: 'Design Around Real Wear',
    text: 'We build collections around how jewelry is actually worn - rings for daily stackability, necklaces for layered necklines, bracelets and bangles for effortless movement, and earrings that transition from day to evening.',
  },
  {
    id: 'evolve',
    num: '02',
    title: 'Craft with Discipline',
    text: `Every piece goes through material, plating, and finish checks before dispatch. We focus on durable construction and refined detailing so each ${BRAND.name} design feels premium up close, not just in photos.`,
  },
  {
    id: 'future',
    num: '03',
    title: 'Earn Trust with Clarity',
    text: 'Luxury should feel transparent. We keep policies straightforward, communicate timelines clearly, and support you before purchase with sizing help so you can choose confidently.',
  },
];

// Values shown in the desktop sticky sidebar
const VALUES = [
  { id: 'quality',       label: 'Uncompromising quality' },
  { id: 'transparency',  label: 'Full supply chain transparency' },
  { id: 'craftsmanship', label: 'Expert craftsmanship' },
  { id: 'timeless',      label: 'Timeless, not trend-driven' },
  { id: 'responsibility',label: 'Responsible manufacturing' },
  { id: 'community',     label: 'Jewelry-first category focus' },
];

// ── Structured data — serialized once at module level ─────────────────────
const PAGE_SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type':       'WebPage',
      '@id':         `${SITE_URL}/story#webpage`,
      url:           `${SITE_URL}/story`,
      name:          `About Luvia | Women's Artificial Jewelry | Founded ${BRAND_YEAR}`,
      description:   `Discover the story behind Luvia - a premium women's jewelry brand for rings, necklaces, bracelets, and earrings. Free shipping above 500. Returns currently unavailable.`,
      inLanguage:    'en-IN',
      isPartOf:      { '@id': `${SITE_URL}/#website` },
      breadcrumb:    { '@id': `${SITE_URL}/story#breadcrumb` },
      /*
        og:type is article — editorial content.
        datePublished helps Google understand content freshness.
      */
      datePublished: `${BRAND_YEAR}-01-01`,
    },
    {
      '@type': 'BreadcrumbList',
      '@id':   `${SITE_URL}/story#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home',      item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Our Story', item: `${SITE_URL}/story` },
      ],
    },
    /*
      Organization schema — highest-value addition for a brand story page.
      Tells Google's Knowledge Graph who Luvia is, when it was founded,
      and what it sells. Enables brand knowledge panel in search results.
    */
    {
      '@type':        'Organization',
      '@id':          `${SITE_URL}/#organization`,
      name:           BRAND.name,
      url:            SITE_URL,
      logo:           `${SITE_URL}/logo.png`,
      foundingDate:   String(BRAND_YEAR),
      description:    `${BRAND.name} is a premium women's jewelry brand specialising in elegant rings, earrings, necklaces, and bracelets. Founded in ${BRAND_YEAR}.`,
      sameAs:         [BRAND.instagram].filter(Boolean),
      contactPoint: {
        '@type':            'ContactPoint',
        contactType:        'customer support',
        availableLanguage:  ['English', 'Hindi'],
      },
    },
  ],
});

// ── Inline SVG icons — zero bundle dependency ─────────────────────────────
function IconArrowRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <line x1="5" y1="12" x2="19" y2="12"/>
      <polyline points="12 5 19 12 12 19"/>
    </svg>
  );
}

function IconCheck() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

// ── Component ──────────────────────────────────────────────────────────────
export default function Story() {
  const pageTitle = `About Luvia | Women's Artificial Jewelry | Founded ${BRAND_YEAR}`;
  const pageDesc  = `Discover the story behind ${BRAND.name} - a premium women's jewelry brand for rings, necklaces, bracelets, and earrings. Free shipping above 500. Returns currently unavailable.`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description"         content={pageDesc} />
        <link rel="canonical"            href={`${SITE_URL}/story`} />
        <meta name="robots"              content="index, follow" />

        {/* Open Graph — article type for editorial content */}
        <meta property="og:type"              content="article" />
        <meta property="og:site_name"         content={BRAND.name} />
        <meta property="og:title"             content={pageTitle} />
        <meta property="og:description"       content={pageDesc} />
        <meta property="og:url"               content={`${SITE_URL}/story`} />
        <meta property="og:image"             content={OG_IMAGE} />
        <meta property="og:image:width"       content="1200" />
        <meta property="og:image:height"      content="630" />
        <meta property="og:locale"            content="en_IN" />
        <meta property="article:published_time" content={`${BRAND_YEAR}-01-01T00:00:00+05:30`} />

        {/* Twitter */}
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image"       content={OG_IMAGE} />

        <script type="application/ld+json">{PAGE_SCHEMA}</script>
      </Helmet>

      <Layout showFooter showBottomTab>
        <main className="story-page" id="main-content">

          {/* ── Full-width hero ────────────────────────────────────────── */}
          <div className="story-hero">
            <div className="story-hero__inner">

              {/* Breadcrumb */}
              <nav className="story-breadcrumb" aria-label="Breadcrumb">
                <ol className="story-breadcrumb__list">
                  <li>
                    <Link to="/" className="story-breadcrumb__link">Home</Link>
                  </li>
                  {/* Valid HTML: non-void <li> with content */}
                  <li aria-hidden="true" className="story-breadcrumb__sep">
                    <span>›</span>
                  </li>
                  <li>
                    <span aria-current="page" className="story-breadcrumb__current">
                      Our Story
                    </span>
                  </li>
                </ol>
              </nav>

              <p className="story-hero__eyebrow" aria-hidden="true">Founded {BRAND_YEAR}</p>
              {/*
                H1 improved: includes brand name + keyword hook.
                "Our Story" as H1 has near-zero search value.
              */}
              <h1 className="story-hero__title">
                The {BRAND.name} Story
              </h1>
              <p className="story-hero__subtitle">
                Born from a belief that jewelry can be both beautiful and responsible -
                premium pieces for daily wear, crafted with intention.
              </p>

            </div>
          </div>

          {/* ── Two-column body layout ─────────────────────────────────── */}
          <div className="story-layout">

            {/* ── Sticky values sidebar (desktop only) ─────────────────── */}
            <aside className="story-sidebar" aria-label="Brand values">
              <div className="story-sidebar__inner">
                <p className="story-sidebar__label">What we stand for</p>
                <ul className="story-sidebar__values">
                  {VALUES.map(({ id, label }) => (
                    <li key={id} className="story-sidebar__value">
                      <span className="story-sidebar__value-icon" aria-hidden="true">
                        <IconCheck />
                      </span>
                      <span>{label}</span>
                    </li>
                  ))}
                </ul>
                <div className="story-sidebar__divider" />
                <Link to="/shop" className="story-sidebar__cta">
                  Shop →
                </Link>
              </div>
            </aside>

            {/* ── Narrative content ─────────────────────────────────────── */}
            <div className="story-main">

              {/* ── How It All Started ─────────────────────────────────── */}
              <section className="story-section" aria-labelledby="beginning-h">
                <h2 id="beginning-h" className="story-section__title">
                  How It All Started
                </h2>
                <div className="story-section__content">
                  <p className="story-section__paragraph">
                    {BRAND.name} was founded in {BRAND_YEAR} to create elevated artificial jewelry for
                    modern wardrobes. We saw a clear gap: women wanted refined rings, necklaces,
                    bracelets, bangles, and earrings that felt premium without feeling inaccessible.
                  </p>
                  <p className="story-section__paragraph">
                    As a new brand, we had the freedom to build intentionally from day one. Not tied to
                    legacy systems, we shaped our process around craftsmanship, quality checks, and design
                    consistency across every collection.
                  </p>
                  <p className="story-section__paragraph">
                    It started with one simple question: <em>"What if everyday jewelry felt truly luxurious?"</em>
                    Better in fit, finish, and wearability. Better in trust. That question became our mission.
                  </p>
                </div>
              </section>

              {/* ── What Inspires Us ───────────────────────────────────── */}
              <section className="story-section story-section--highlight" aria-labelledby="inspiration-h">
                <h2 id="inspiration-h" className="story-section__title">
                  What Inspires Us
                </h2>
                <div className="story-section__content">
                  <p className="story-section__paragraph">
                    We are inspired by timeless silhouettes and clean proportions - pieces that do not
                    rely on trends to feel relevant. Jewelry you reach for repeatedly because it works
                    with real life, not just occasion dressing.
                  </p>
                  <p className="story-section__paragraph">
                    Our design philosophy is simple: <strong>less is more.</strong> We perfect the
                    essentials. Structured rings, balanced chain lengths, comfortable bracelet fits, and
                    versatile earrings engineered for repeat wear.
                  </p>
                  <p className="story-section__paragraph">
                    We are also inspired by customers who choose intentionally - people who value quality,
                    transparency, and long-term style over impulse trends. That is exactly who we design for.
                  </p>
                </div>
              </section>

              {/* ── Our Approach ───────────────────────────────────────── */}
              <section className="story-section" aria-labelledby="approach-h">
                <h2 id="approach-h" className="story-section__title">Our Approach</h2>
                <div className="story-approach">
                  {APPROACH_ITEMS.map(({ id, num, title, text }) => (
                    <div key={id} className="story-approach__item">
                      <div className="story-approach__number" aria-hidden="true">{num}</div>
                      <div className="story-approach__content">
                        <h3 className="story-approach__title">{title}</h3>
                        <p  className="story-approach__text">{text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/*
                Mid-funnel nudge — after reading the approach,
                readers have highest purchase intent.
                Previously there was no CTA until the very bottom.
              */}
              <div className="story-nudge">
                <p className="story-nudge__text">
                  See what we've built so far →
                </p>
                <Link to="/shop" className="story-nudge__link">
                  Shop
                  <IconArrowRight />
                </Link>
              </div>

              {/* ── Looking Ahead ──────────────────────────────────────── */}
              <section className="story-section" aria-labelledby="future-h">
                <h2 id="future-h" className="story-section__title">Looking Ahead</h2>
                <div className="story-section__content">
                  <p className="story-section__paragraph">
                    {BRAND_YEAR} is just the beginning. Our immediate focus is precision - refining product
                    quality, fit guidance, and category depth across rings, necklaces, bracelets, and earrings.
                  </p>
                  <p className="story-section__paragraph">
                    In the coming years you will see thoughtful expansion with the same standards: premium
                    finishes, clear material disclosures, and responsible production decisions.
                  </p>
                  <p className="story-section__paragraph">
                    We currently offer free shipping on orders above 500, and returns are temporarily unavailable
                    while we strengthen operations. We would rather be transparent today and better tomorrow.
                  </p>
                </div>
              </section>

              {/* ── What We Believe ────────────────────────────────────── */}
              {/*
                Renamed from "Why Now?" — macro industry trends don't convert.
                Concrete brand values and a human voice do.
              */}
              <section className="story-section story-section--highlight" aria-labelledby="values-h">
                <h2 id="values-h" className="story-section__title">What We Believe</h2>
                <div className="story-section__content">
                  <p className="story-section__paragraph">
                    We believe premium jewelry should balance beauty, durability, and value. You should not
                    have to compromise on finishing, comfort, or trust to build a collection you love.
                  </p>
                  <p className="story-section__paragraph">
                    We believe daily-wear jewelry deserves the same care as occasion pieces. Every ring,
                    necklace, bracelet, and earring should feel intentional, flattering, and well made.
                  </p>
                  <p className="story-section__paragraph">
                    We believe modern brands should speak clearly about what they can and cannot offer today.
                    That is why we are explicit about shipping thresholds, sizing guidance, and current return policy.
                    <strong> That is {BRAND.name}.</strong>
                  </p>
                </div>
              </section>

              {/* ── CTA ────────────────────────────────────────────────── */}
              <section className="story-cta" aria-labelledby="cta-h">
                <h2 id="cta-h" className="story-cta__title">Be Part of Our Story</h2>
                <p className="story-cta__text">
                  Discover refined jewelry essentials with free shipping on orders above 500.
                </p>
                <div className="story-cta__actions">
                  {/*
                    Primary CTA: collection (direct conversion intent).
                    Previous: "Learn More → /about" (another editorial page,
                    no conversion value, competes with /story for brand searches).
                  */}
                  <Link to="/shop" className="story-cta__btn story-cta__btn--primary">
                    Shop
                    <IconArrowRight />
                  </Link>
                  <Link to="/contact" className="story-cta__btn story-cta__btn--ghost">
                    Get in Touch
                    <IconArrowRight />
                  </Link>
                </div>
              </section>

            </div>{/* /story-main */}
          </div>{/* /story-layout */}

        </main>
      </Layout>
    </>
  );
}